/**
 * Infer the stage from a hostname.
 *
 * There were three ad-hoc versions of this before the package, and each was
 * wrong in a different way:
 *
 * - `apps/dashboard/hooks/use-apps-manifest.ts` used `host.includes("l.")`,
 *   which matches any app subdomain ending in `l` — `model.gaz.codes` would
 *   have resolved as *local* — and classified bare `localhost` as **prod**.
 * - `packages/observability/src/browser.ts` used
 *   `host.startsWith("dev.") || host.includes("-dev.")`, which matches neither
 *   of the planned `<app>.dev.gaz.codes` hostnames, so every dev page would
 *   have been classified prod and sampled at 0.2 instead of 1.0.
 *
 * Match on **label boundaries**, never substrings.
 */

import type { Stage } from "./types.js";

/** Hostnames that mean "a developer's own machine" regardless of domain. */
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);

/**
 * Classify a hostname into a stage.
 *
 * The stage label is the one immediately left of the base domain:
 * `chat.l.gaz.codes` → `l` → local; `chat.dev.gaz.codes` → `dev` → dev;
 * `chat.gaz.codes` → no label → prod. An unrecognised host defaults to
 * `prod`, which is the safe direction: prod settings are the conservative
 * ones (sampling down, no debug surfaces).
 */
export function detectStageFromHostname(
  hostname: string,
  baseDomain = "gaz.codes",
): Stage {
  const host = hostname.toLowerCase().replace(/\.$/, "");

  if (LOOPBACK_HOSTS.has(host) || host.endsWith(".localhost")) return "local";

  if (host !== baseDomain && !host.endsWith(`.${baseDomain}`)) return "prod";

  // Labels to the left of the base domain, e.g. `chat.l` for `chat.l.gaz.codes`.
  const prefix = host === baseDomain ? "" : host.slice(0, -(baseDomain.length + 1));
  if (prefix === "") return "prod";

  const stageLabel = prefix.split(".").pop();
  if (stageLabel === "l") return "local";
  if (stageLabel === "dev") return "dev";
  return "prod";
}

/** Browser-safe wrapper; returns `prod` during SSR where there is no `window`. */
export function detectStage(baseDomain = "gaz.codes"): Stage {
  if (typeof window === "undefined") return "prod";
  return detectStageFromHostname(window.location.hostname, baseDomain);
}

/**
 * The apex hostname of the stage `hostname` belongs to — where `web`, the
 * platform's installable app, is served for that stage:
 * `chat.l.gaz.codes` → `l.gaz.codes`, `chat.dev.gaz.codes` → `dev.gaz.codes`,
 * `chat.gaz.codes` → `gaz.codes`.
 *
 * `null` for anything not under `baseDomain` (loopback included): a page
 * reached by IP or port has no stage apex to point at, and guessing one would
 * send the user to a different stage than the one they are on.
 */
export function stageApexHostname(hostname: string, baseDomain = "gaz.codes"): string | null {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  if (host === baseDomain) return baseDomain;
  if (!host.endsWith(`.${baseDomain}`)) return null;

  const stageLabel = host.slice(0, -(baseDomain.length + 1)).split(".").pop();
  return stageLabel === "l" || stageLabel === "dev" ? `${stageLabel}.${baseDomain}` : baseDomain;
}
